import React from 'react';
import { Phone, Calendar, Award, MapPin, Users } from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../data/mock';
import { useAppointment } from '../context/AppointmentContext';

const Hero = () => {
  const { openAppointmentModal } = useAppointment();
  
  return (
    <section className="relative bg-gradient-to-br from-white via-red-50/20 to-teal-50/30 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#20B2AA]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E63946]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Overtitle */}
            <div className="inline-flex items-center gap-2 bg-white border-2 border-[#20B2AA] text-[#1B1B3A] px-5 py-2.5 rounded-full font-semibold shadow-sm">
              <Award className="h-4 w-4" />
              <span className="text-sm">{heroData.overtitle}</span>
            </div>

            {/* Main Headline - Outcome Focused */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {heroData.headline}
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-gray-600 leading-relaxed">
              {heroData.subheadline}
            </p>

            {/* CTA Buttons Group */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                size="lg" 
                className="bg-[#E63946] hover:bg-[#D32F3D] text-white text-lg px-8 py-7 shadow-xl hover:shadow-2xl transition-all font-bold"
                onClick={openAppointmentModal}
                data-testid="hero-schedule-btn"
              >
                <Calendar className="mr-3 h-6 w-6" />
                {heroData.primaryCTA}
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white text-lg px-8 py-7 font-bold"
                asChild
              >
                <a href="tel:+14043552728">
                  <Phone className="mr-3 h-6 w-6" />
                  {heroData.secondaryCTA}
                </a>
              </Button>
            </div>

            {/* Trust Badges - Single Row with Smaller Font */}
            <div className="flex flex-wrap items-center gap-6 pt-8 border-t-2 border-gray-200">
              {heroData.trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="text-xl font-bold text-[#E63946]">
                    {badge.value}
                  </div>
                  <div className="text-xs text-gray-600 font-medium">
                    {badge.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={heroData.heroImage}
                srcSet="/webp-images/hero-image-800.webp 800w, /webp-images/hero-image-1200.webp 1200w, /webp-images/hero-image-1600.webp 1600w"
                sizes="(max-width: 768px) 100vw, 50vw"
                alt="Regenerative Medicine Treatment Atlanta"
                className="w-full h-[600px] object-cover"
                fetchpriority="high"
                loading="eager"
                width="1200"
                height="800"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B3A]/30 via-transparent to-transparent"></div>
            </div>

            {/* Floating Stats Card - Bottom Left (inside image bounds) */}
            <div className="absolute bottom-4 left-4 bg-white rounded-2xl shadow-2xl p-5 border-t-4 border-[#20B2AA]">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-[#20B2AA] to-[#1B9E97] p-3 rounded-xl">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">10,000+</div>
                  <div className="text-xs text-gray-600 font-medium">Successful Treatments</div>
                </div>
              </div>
            </div>

            {/* Floating Location Badge - Top Right (inside image bounds) */}
            <div className="absolute top-4 right-4 bg-white rounded-2xl shadow-2xl p-5 border-t-4 border-[#E63946]">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-[#E63946] to-[#D32F3D] p-3 rounded-xl">
                  <MapPin className="h-8 w-8 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">6</div>
                  <div className="text-xs text-gray-600 font-medium">Locations</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
